import React, { useRef, useState } from 'react';
import styles from './rating-stars.module.scss';
import cl from 'classnames';
import { RatingStarsProps } from './rating-stars.types';

const STAR_PATH = 'M10,16.387l5.571,3.529a.547.547,0,0,0,.808-.611L14.9,12.654l4.914-4.468a.575.575,0,0,0-.313-1l-6.476-.577L10.493.348a.527.527,0,0,0-.991,0L6.969,6.614.493,7.191a.573.573,0,0,0-.3,1L5.1,12.654,3.629,19.305a.547.547,0,0,0,.808.611Z';
const EMPTY_COLOR = '#5a5a5a';
const MIN_RATING = 1;

type PartialStarProps = {
  fillPercentage: number;
  gradientId: string;
};

const PartialStar = ({
  fillPercentage,
  gradientId,
}: PartialStarProps) => {
  const clampedFill = Math.min(100, Math.max(0, fillPercentage));

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset={`${clampedFill}%`} stopColor={styles.primary} />
          <stop offset={`${clampedFill}%`} stopColor={EMPTY_COLOR} />
        </linearGradient>
      </defs>
      <path fill={`url(#${gradientId})`} d={STAR_PATH} />
    </svg>
  );
};

const getStarFillPercentage = (rating: number, starNumber: number): number => {
  if (rating >= starNumber) {
    return 100;
  }

  if (rating <= starNumber - 1) {
    return 0;
  }

  return (rating - (starNumber - 1)) * 100;
};

const getRatingFromEvent = (e: React.MouseEvent, index: number): number => {
  const rect = e.currentTarget.getBoundingClientRect();
  const isLeftHalf = (e.clientX - rect.left) < rect.width / 2;
  const value = isLeftHalf ? index + 0.5 : index + 1;

  return Math.max(MIN_RATING, value);
};

const RatingStarsComponent = (props: RatingStarsProps) => {
  const {
    rating,
    reviewsCount: count,
    className,
    style,
    selfRating,
    isAdmin,
    rate,
    disabled
  } = props;

  const [localRating, setLocalRating] = useState(rating);
  const [hoverIndex, setHoverIndex] = useState(0);
  const gradientBaseId = useRef(`rating-stars-${Math.random().toString(36).slice(2, 9)}`).current;

  const ratingText = rating === null ? '' : rating;
  const countText = count === undefined ? '' : `(${count})`;
  const isSelf = selfRating === true;
  const isInteractive = isSelf && !isAdmin && !disabled;

  const onRate = (e: React.MouseEvent, index: number) => {
    if (isSelf && !isAdmin && rate && !disabled) {
      const value = getRatingFromEvent(e, index);
      rate(value);
      setLocalRating(value);
    }
  };

  const onHover = (e: React.MouseEvent, index: number) => {
    if (isSelf && !isAdmin && !disabled) {
      const value = getRatingFromEvent(e, index);
      setHoverIndex(value);
    }
  };

  const onMouseLeave = () => {
    setHoverIndex(0);
  };

  return (
    <div
      className={cl(styles.ratingStars, className)}
      style={style}
      onMouseLeave={onMouseLeave}
    >
      {Array(5).fill('').map((_, index) => {
        const starNumber = index + 1;
        let dynRate = hoverIndex > 0 ? hoverIndex : localRating;
        if (dynRate == null) {
          dynRate = 0;
        }

        const fillPercentage = getStarFillPercentage(dynRate, starNumber);

        return (
          <span
            key={index}
            className={cl(styles.starWrapper, {
              [styles.starWrapperInteractive]: isInteractive,
            })}
            onClick={(e) => onRate(e, index)}
            onMouseMove={(e) => onHover(e, index)}
          >
            <PartialStar
              fillPercentage={fillPercentage}
              gradientId={`${gradientBaseId}-star-${index}`}
            />
          </span>
        );
      })}
      {!isSelf && <span>{ratingText} {countText}</span>}
    </div>
  );
};

const RatingStars = RatingStarsComponent;
export { RatingStars };
